import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import jwtDecode from "jwt-decode";
import { useCustomStorage } from "../hooks/useCustomStorage";
import { Rotation, useOrientation } from "../hooks/useOrientation";
import api from "../services/api";
import { clearStore } from "../services/db";
import { refreshSession, signInService } from "../services/sessions";
import type { Midia } from "../types/session";

type AuthProviderProps = {
  children: React.ReactNode;
};

export type AuthState = {
  token: string;
  refreshToken?: string;
  midia?: Midia;
};

export type AuthContextData = {
  token: string;
  loading: boolean;
  midia?: Midia;
  loadStorageData(): Promise<void>;
  signIn(code: string): Promise<void>;
  refreshToken(): Promise<void>;
  persistUser(token: string): Promise<void>;
  signOut(): void;
};

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const storage = useCustomStorage();
  const { handleChangeOrientation } = useOrientation();
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async () => {
    await clearStore();
    setData({} as AuthState);
    const __orientation = (localStorage.getItem("@datasales:orientation") ||
      0) as Rotation;
    storage.clear();
    handleChangeOrientation(__orientation);
    location.reload();
  }, []);

  useEffect(() => {
    loadStorageData();
  }, []);

  const loadStorageData = async (): Promise<void> => {
    try {
      const token = storage.getItem("@datasales:token");
      const refreshToken = storage.getItem("@datasales:refresh_token");
      const storedMidia = storage.getItem("@datasales:midia");

      if (token && storedMidia) {
        api.defaults.headers.common.authorization = token;

        let midia = storedMidia;
        try {
          const parsed: any = jwtDecode(token);
          midia = { ...storedMidia, radioShortId: parsed?.radioShortId ?? storedMidia.radioShortId ?? null };
        } catch {}

        setData({ token, refreshToken, midia });
      }
    } catch (err) {
      console.log(err);
      signOut();
    } finally {
      setLoading(false);
    }
  };

  const signIn = useCallback(async (code: string) => {
    const response = await signInService(code);

    const { token, refreshToken, midia } = response;

    api.defaults.headers.common.authorization = `${token}`;

    storage.setItem("@datasales:token", token);
    storage.setItem("@datasales:refresh_token", refreshToken);

    setData({ token, refreshToken, midia });
  }, []);

  const refreshToken = useCallback(async () => {
    const _refreshToken = storage.getItem("@datasales:refresh_token");
    const response = await refreshSession(_refreshToken);

    const { token, refreshToken, midia } = response;

    api.defaults.headers.common.authorization = `${token}`;

    storage.setItem("@datasales:token", token);
    storage.setItem("@datasales:refresh_token", refreshToken);
    storage.setItem("@datasales:midia", JSON.stringify({ ...midia }));

    setData({ token, refreshToken, midia });
    location.reload();
  }, []);

  const persistUser = useCallback(async (token: string) => {
    storage.setItem("@datasales:token", token);

    setData({ token });
  }, []);

  const authContextData = useMemo(() => {
    return {
      token: data.token,
      midia: data.midia,
      loading,
      signIn,
      refreshToken,
      signOut,
      persistUser,
      loadStorageData,
    };
  }, [
    data.token,
    data.midia,
    loading,
    signIn,
    refreshToken,
    signOut,
    persistUser,
    loadStorageData,
  ]);

  return (
    <AuthContext.Provider value={authContextData}>
      {children}
    </AuthContext.Provider>
  );
};
